import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ShoppingBasket, User } from "lucide-react";
import { STORE } from "../lib/api";
import { useShop } from "../lib/shop";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/products", label: "Our Products" },
    { to: "/about", label: "About Us" },
    { to: "/contact", label: "Contact Us" },
];

export const Header = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { basketCount, user } = useShop();

    const linkClass = ({ isActive }) =>
        `px-4 py-2 rounded-full text-sm font-bold transition-colors ${
            isActive
                ? "bg-[#0033A0] text-white"
                : "text-slate-700 hover:text-[#0033A0] hover:bg-slate-100"
        }`;

    return (
        <header data-testid="site-header" className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 md:h-20">
                    <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0" onClick={() => setOpen(false)}>
                        <img
                            src="/assets/logo.webp"
                            alt="Freddie's Afro Caribbean Store logo"
                            className="h-10 md:h-12 w-auto rounded-md"
                            data-testid="header-logo"
                        />
                    </Link>

                    <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav" aria-label="Main navigation">
                        {NAV.map((item) => (
                            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                {item.label}
                            </NavLink>
                        ))}
                        <a
                            href={`tel:${STORE.phones[0].tel}`}
                            data-testid="header-call-button"
                            className="ml-3 hidden lg:inline-flex items-center gap-2 rounded-full bg-[#40E0D0] px-4 py-2 text-sm font-bold text-[#0033A0] transition-colors hover:bg-[#2fd4c4]"
                        >
                            <Phone className="h-4 w-4" />
                            {STORE.phones[0].label}
                        </a>
                        <Link
                            to="/account"
                            data-testid="header-account-link"
                            aria-label="Your account"
                            className="ml-2 inline-flex items-center justify-center rounded-full p-2.5 text-[#0033A0] hover:bg-slate-100 transition-colors"
                        >
                            <User className="h-5 w-5" />
                        </Link>
                        <Link
                            to="/basket"
                            data-testid="header-basket-link"
                            aria-label="Your basket"
                            className="relative ml-1 inline-flex items-center justify-center rounded-full p-2.5 text-[#0033A0] hover:bg-slate-100 transition-colors"
                        >
                            <ShoppingBasket className="h-5 w-5" />
                            {basketCount > 0 && (
                                <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#40E0D0] px-1 text-xs font-extrabold text-[#0033A0]" data-testid="basket-count">
                                    {basketCount}
                                </span>
                            )}
                        </Link>
                    </nav>

                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        className="md:hidden inline-flex items-center justify-center rounded-full p-2 text-[#0033A0] hover:bg-slate-100"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            {open && (
                <nav className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1" data-testid="mobile-nav" aria-label="Mobile navigation">
                    {NAV.map((item) => {
                        const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`block rounded-xl px-4 py-3 text-base font-bold transition-colors ${
                                    active ? "bg-[#0033A0] text-white" : "text-slate-700 hover:bg-slate-100"
                                }`}
                            >
                                {item.label}
                            </Link>
                        );
                    })}
                    <a
                        href={`tel:${STORE.phones[0].tel}`}
                        data-testid="mobile-call-button"
                        className="flex items-center gap-2 rounded-xl bg-[#40E0D0] px-4 py-3 text-base font-bold text-[#0033A0]"
                    >
                        <Phone className="h-5 w-5" />
                        Call {STORE.phones[0].label}
                    </a>
                    <div className="flex gap-2">
                        <Link
                            to="/basket"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-basket-link"
                            className="flex-1 flex items-center justify-center gap-2 rounded-xl border-2 border-[#0033A0] px-4 py-3 text-base font-bold text-[#0033A0]"
                        >
                            <ShoppingBasket className="h-5 w-5" />
                            Basket{basketCount > 0 ? ` (${basketCount})` : ""}
                        </Link>
                        <Link
                            to="/account"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-account-link"
                            className="flex-1 flex items-center justify-center gap-2 rounded-xl border-2 border-[#0033A0] px-4 py-3 text-base font-bold text-[#0033A0]"
                        >
                            <User className="h-5 w-5" />
                            {user ? "Account" : "Sign in"}
                        </Link>
                    </div>
                </nav>
            )}
        </header>
    );
};
